<script setup lang="ts">
/**
 * How long an offer has left, as a <peppermill-badge>. Renders nothing for an offer with no readable end date, or one
 * that has already been redeemed - see src\helpers\getOfferExpiry.
 *
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add App\components\offers\OfferExpiryStatus.vue. OFFER_APPLICABLE_END_TS was on every offer and shown nowhere.
 */

import PeppermillBadge from '@framework/components/PeppermillBadge.vue';
import type { PeppermillBadgeMod } from '@framework/components/PeppermillBadge.vue';
import type { PropType } from 'vue';
import { computed } from 'vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getOfferExpiry, { offerExpiryVariants } from '@/helpers/getOfferExpiry';

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
  isSmall: {
    type: Boolean,
    default: false,
  },
});

const expiry = computed(() => getOfferExpiry(props.offer));

//Solid for the two states worth interrupting a guest over, subtle for the rest.
const mod = computed<PeppermillBadgeMod>(() => (['today', 'soon'].includes(expiry.value.urgency) ? 'solid' : 'subtle'));

const tooltip = computed<string | undefined>(() => {
  if (!expiry.value.date) {
    return undefined;
  }

  return (
    'Offer valid through ' +
    new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(expiry.value.date)
  );
});
</script>

<template>
  <peppermill-badge
    v-if="expiry.hasExpiry"
    :label="expiry.label"
    :icon="expiry.icon"
    :variant="offerExpiryVariants[expiry.urgency]"
    :mod="mod"
    :is-small="isSmall"
    :tooltip="tooltip"
  ></peppermill-badge>
</template>
